import type {
  IDesignStoreFormShape,
  IStoreInfoFormShape,
} from '../StoreDetails/storeDetails.types';
import type { ISizesFormValues } from '../StoreConfirmSizes/helpers/storeConfirmSizes.types';
import {
  convertOrderItemSizesToAllowedSizes,
  convertSizesToOrderItemFormat,
} from '../StoreConfirmSizes/helpers/storeSizeConfig.utils';
import { buildStoreCustomizationUpdatePayload } from './storeCheckoutCustomizationPayload.utils';
import type { IStoreCustomizationBrandingState } from './storeCheckoutCustomizationPayload.utils';
import { buildStoreShippingAddressPayload } from './storeCheckoutShippingAddressUpdate.utils';
import type { IStoreShippingAddressFormValues } from './storeCheckoutShippingAddressUpdate.utils';
import type { ICartItemDetail, ICartResponse } from '@/common/interfaces/cart.interface';
import type { IUpdateStoreRequest } from '@/common/interfaces/store.interface';

/**
 * Bulk (ship-to-one-place) delivery address is the only shipping field editable on the
 * live-store edit page; the shipping method stays locked. For any other shipping option
 * there is no single store-level address, so the address is not tracked.
 */
export const BULK_SHIPPING_OPTION = 'one-place';

export interface IStoreCheckoutEditShippingSnapshot {
  option: string;
  address: IStoreShippingAddressFormValues;
}

export interface IStoreCheckoutEditBaseline {
  storeInfo: IStoreInfoFormShape;
  designStore: IDesignStoreFormShape;
  sizes: ISizesFormValues;
  cartItems: string;
  shippingAddress: string;
}

export const serializeEditableCartItems = (items: ICartResponse): string =>
  JSON.stringify(
    items.map((item) => ({
      id: item.id,
      apparelName: item.apparelName ?? '',
      productTitle: item.productTitle ?? '',
    }))
  );

/** Only the bulk delivery address is editable; ignore other options so they never look dirty. */
export const serializeEditableShippingAddress = (
  shipping: IStoreCheckoutEditShippingSnapshot
): string => {
  if (shipping.option !== BULK_SHIPPING_OPTION) {
    return '';
  }

  const { address } = shipping;

  return JSON.stringify({
    shippingName: address.shippingName?.trim() ?? '',
    attentionName: address.attentionName?.trim() ?? '',
    street1: address.street1?.trim() ?? '',
    street2: address.street2?.trim() ?? '',
    city: address.city?.trim() ?? '',
    state: address.state?.value?.trim() ?? '',
    shippingZipCode: address.shippingZipCode?.trim() ?? '',
  });
};

export const serializeEditableDesignStore = (designStore: IDesignStoreFormShape): string =>
  JSON.stringify({
    personalizeToggle: designStore.personalizeToggle,
    selectedColor: designStore.selectedColor,
    storeLogo: designStore.storeLogo,
    selectedColors: designStore.selectedColors,
    selectedVibe: designStore.selectedVibe,
  });

export const serializeEditableSizes = (sizes: ISizesFormValues): string =>
  JSON.stringify(
    sizes.sizes.map((group) => ({
      orderItemId: group.orderItemId,
      sizesSelected: Object.fromEntries(
        Object.entries(group.sizesSelected).map(([label, size]) => [
          label,
          {
            selected: size.selected,
            lockedByExistingOrder: size.lockedByExistingOrder ?? false,
          },
        ])
      ),
    }))
  );

export const buildStoreCheckoutEditBaseline = (input: {
  storeInfo: IStoreInfoFormShape;
  designStore: IDesignStoreFormShape;
  sizes: ISizesFormValues;
  cartItems: ICartResponse;
  shipping: IStoreCheckoutEditShippingSnapshot;
}): IStoreCheckoutEditBaseline => ({
  storeInfo: { ...input.storeInfo },
  designStore: { ...input.designStore },
  sizes: JSON.parse(JSON.stringify(input.sizes)) as ISizesFormValues,
  cartItems: serializeEditableCartItems(input.cartItems),
  shippingAddress: serializeEditableShippingAddress(input.shipping),
});

export const hasStoreCheckoutEditChanges = (
  baseline: IStoreCheckoutEditBaseline | null,
  current: {
    storeInfo: IStoreInfoFormShape;
    designStore: IDesignStoreFormShape;
    sizes: ISizesFormValues;
    cartItems: ICartResponse;
    shipping: IStoreCheckoutEditShippingSnapshot;
  }
): boolean => {
  if (!baseline) {
    return false;
  }

  return (
    JSON.stringify(current.storeInfo) !== JSON.stringify(baseline.storeInfo) ||
    serializeEditableDesignStore(current.designStore) !==
      serializeEditableDesignStore(baseline.designStore) ||
    serializeEditableSizes(current.sizes) !== serializeEditableSizes(baseline.sizes) ||
    serializeEditableCartItems(current.cartItems) !== baseline.cartItems ||
    serializeEditableShippingAddress(current.shipping) !== baseline.shippingAddress
  );
};

const buildBaselineProductTitleById = (serializedCartItems: string): Map<number, string> => {
  const titleById = new Map<number, string>();

  try {
    const parsed = JSON.parse(serializedCartItems) as {
      id: number;
      productTitle?: string;
    }[];
    parsed.forEach((item) => titleById.set(item.id, item.productTitle ?? ''));
  } catch {
    /* baseline cart items are always serialized by serializeEditableCartItems */
  }

  return titleById;
};

const buildOrderItemProductTitleUpdates = (
  cartItems: ICartResponse,
  baselineSerializedCartItems: string
): IUpdateStoreRequest['order_items'] => {
  const baselineTitleById = buildBaselineProductTitleById(baselineSerializedCartItems);

  return cartItems
    .map((item) => {
      const currentTitle = item.productTitle ?? '';
      const baselineTitle = baselineTitleById.get(item.id);

      if (baselineTitle === undefined || currentTitle === baselineTitle) {
        return null;
      }

      return { id: item.id, product_title: currentTitle };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);
};

const mergeOrderItemUpdates = (
  sizeUpdates: IUpdateStoreRequest['order_items'],
  titleUpdates: IUpdateStoreRequest['order_items']
): NonNullable<IUpdateStoreRequest['order_items']> => {
  const updatesById = new Map<number, NonNullable<IUpdateStoreRequest['order_items']>[number]>();

  (sizeUpdates ?? []).forEach((update) => updatesById.set(update.id, { ...update }));

  (titleUpdates ?? []).forEach((update) => {
    const existing = updatesById.get(update.id);
    updatesById.set(update.id, existing ? { ...existing, ...update } : { ...update });
  });

  return Array.from(updatesById.values());
};

const findCartItemForSizeGroup = (
  cartItems: ICartResponse,
  orderItemId: string
): ICartItemDetail | undefined =>
  cartItems.find(
    (item) =>
      String(item.cartItemId ?? item.proofItemId ?? item.id) === orderItemId ||
      String(item.id) === orderItemId
  );

const buildOrderItemSizeUpdates = (
  sizes: ISizesFormValues,
  cartItems: ICartResponse
): IUpdateStoreRequest['order_items'] =>
  sizes.sizes
    .map((group) => {
      const cartItem = findCartItemForSizeGroup(cartItems, group.orderItemId);
      if (!cartItem) {
        return null;
      }

      const sizeCodeSource = cartItem.designApparelSizes || cartItem.apparelSizes || '';
      const sizesString = convertSizesToOrderItemFormat(group.sizesSelected, sizeCodeSource);
      const allowedSizesString = convertOrderItemSizesToAllowedSizes(sizesString);

      return {
        id: cartItem.id,
        order_item_id:
          cartItem.alphaNumericProofItemId ?? String(cartItem.cartItemId ?? cartItem.id),
        sizes: sizesString,
        allowed_sizes: allowedSizesString,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

export const buildStoreEditSavePayload = (input: {
  orderId: number;
  baseline: IStoreCheckoutEditBaseline;
  current: {
    storeInfo: IStoreInfoFormShape;
    designStore: IDesignStoreFormShape;
    sizes: ISizesFormValues;
    branding: IStoreCustomizationBrandingState;
    cartItems: ICartResponse;
    shipping: IStoreCheckoutEditShippingSnapshot;
  };
}): IUpdateStoreRequest | null => {
  const payload: IUpdateStoreRequest = {
    order_id: input.orderId,
  };
  let hasChanges = false;

  if (input.current.storeInfo.storeName !== input.baseline.storeInfo.storeName) {
    payload.name = input.current.storeInfo.storeName;
    hasChanges = true;
  }

  if (input.current.storeInfo.storeDescription !== input.baseline.storeInfo.storeDescription) {
    payload.description = input.current.storeInfo.storeDescription;
    hasChanges = true;
  }

  if (input.current.storeInfo.displayMadeWith !== input.baseline.storeInfo.displayMadeWith) {
    payload.display_made_with_love = input.current.storeInfo.displayMadeWith ? 1 : 0;
    hasChanges = true;
  }

  if (
    serializeEditableDesignStore(input.current.designStore) !==
    serializeEditableDesignStore(input.baseline.designStore)
  ) {
    Object.assign(
      payload,
      buildStoreCustomizationUpdatePayload(input.orderId, input.current.branding)
    );
    hasChanges = true;
  }

  const sizesChanged =
    serializeEditableSizes(input.current.sizes) !== serializeEditableSizes(input.baseline.sizes);
  const cartItemsChanged =
    serializeEditableCartItems(input.current.cartItems) !== input.baseline.cartItems;

  if (sizesChanged || cartItemsChanged) {
    const sizeUpdates = sizesChanged
      ? buildOrderItemSizeUpdates(input.current.sizes, input.current.cartItems)
      : [];
    const titleUpdates = cartItemsChanged
      ? buildOrderItemProductTitleUpdates(input.current.cartItems, input.baseline.cartItems)
      : [];
    const orderItemUpdates = mergeOrderItemUpdates(sizeUpdates, titleUpdates);

    if (orderItemUpdates.length > 0) {
      payload.order_items = orderItemUpdates;
      hasChanges = true;
    }
  }

  const shippingAddressChanged =
    serializeEditableShippingAddress(input.current.shipping) !== input.baseline.shippingAddress;

  if (shippingAddressChanged && input.current.shipping.option === BULK_SHIPPING_OPTION) {
    payload.address = buildStoreShippingAddressPayload(input.current.shipping.address);
    hasChanges = true;
  }

  return hasChanges ? payload : null;
};
